// Client-side billing: plan persistence and calls to the server's Stripe
// endpoints. The "token" in a plan is a server-signed entitlement — it can't
// be forged by editing localStorage, and the server re-checks subscriptions
// against Stripe when tokens are refreshed.

export const API_URL = process.env.REACT_APP_API_URL || '';

const PLAN_KEY = 'propdoc_plan';
const PENDING_KEY = 'propdoc_pending';

export function loadPlan() {
  try {
    const plan = JSON.parse(localStorage.getItem(PLAN_KEY) || 'null');
    return plan && plan.token ? plan : null;
  } catch {
    return null;
  }
}

export function savePlan(plan) {
  localStorage.setItem(PLAN_KEY, JSON.stringify(plan));
}

export function clearPlan() {
  localStorage.removeItem(PLAN_KEY);
}

export function formatPeriodEnd(periodEnd) {
  if (!periodEnd) return null;
  return new Date(periodEnd * 1000).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export function isProActive(plan) {
  return !!(
    plan
    && plan.token
    && plan.type?.startsWith('pro')
    && (!plan.exp || plan.exp * 1000 > Date.now())
  );
}

// The in-flight report survives the full-page redirect to Stripe via
// sessionStorage: { reportId, filename, singleToken? }.
export function loadPending() {
  try {
    return JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
  } catch {
    return null;
  }
}

export function savePending(pending) {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
}

export function clearPending() {
  sessionStorage.removeItem(PENDING_KEY);
}

async function jsonOrThrow(res, fallbackMessage) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || fallbackMessage);
  return data;
}

export async function startCheckout(planType, reportId) {
  const res = await fetch(`${API_URL}/api/billing/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan: planType, reportId: reportId || null }),
  });
  const data = await jsonOrThrow(res, 'Could not start checkout. Please try again.');
  if (!data.url) throw new Error('Could not start checkout. Please try again.');
  return data.url;
}

// Resolves to { plan } for plan purchases or { topup } for extra-report packs.
export async function confirmCheckout(sessionId) {
  const res = await fetch(
    `${API_URL}/api/billing/confirm?session_id=${encodeURIComponent(sessionId)}`
  );
  return jsonOrThrow(
    res,
    "We couldn't confirm your payment. If you were charged, please contact support."
  );
}

// Start checkout for 10 extra reports this month (Pro monthly only).
export async function startTopup(plan) {
  const res = await fetch(`${API_URL}/api/billing/topup`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${plan.token}` },
  });
  const data = await jsonOrThrow(res, 'Could not start checkout. Please try again.');
  if (!data.url) throw new Error('Could not start checkout. Please try again.');
  return data.url;
}

// { metered, used, limit, topupReports } for Pro monthly; { metered: false } otherwise.
export async function fetchUsage(plan) {
  const res = await fetch(`${API_URL}/api/billing/usage`, {
    headers: { Authorization: `Bearer ${plan.token}` },
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

export async function refreshPlan(plan) {
  const res = await fetch(`${API_URL}/api/billing/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: plan.token }),
  });
  if (res.status === 401 || res.status === 402) return null; // subscription gone
  if (!res.ok) return plan; // server hiccup — keep the current plan
  const data = await res.json().catch(() => null);
  return data?.plan || plan;
}

export async function cancelPlan(plan) {
  const res = await fetch(`${API_URL}/api/billing/cancel`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${plan.token}` },
  });
  const data = await jsonOrThrow(res, 'Could not cancel the plan. Please try again.');
  return data.periodEnd;
}

// Email magic-link sign-in: the account is the email used at checkout.
// Signing in on any device issues the same Pro entitlement token there.
export async function requestLogin(email) {
  const res = await fetch(`${API_URL}/api/billing/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await jsonOrThrow(res, 'Sign-in failed. Please try again.');
  return data.message;
}

export async function confirmLogin(code) {
  const res = await fetch(`${API_URL}/api/billing/login/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  const data = await jsonOrThrow(res, 'That sign-in link has expired — request a new one.');
  return data.plan;
}

// Legacy names kept for the ?restore= links in already-sent emails.
export const requestRestore = requestLogin;

export async function confirmRestore(code) {
  const res = await fetch(`${API_URL}/api/billing/restore/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  const data = await jsonOrThrow(res, 'That restore link has expired — request a new one.');
  return data.plan;
}

export async function fetchReport(reportId, token) {
  const res = await fetch(`${API_URL}/api/report/${encodeURIComponent(reportId)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => ({}));
  return { status: res.status, data };
}
